import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
} from '@mui/material';
import SingleDatePickerMAIQ from 'app/components/SingleDatePickerMAIQ';
import { getTodayStartOfDay } from 'utils/dateUtils';

export interface FooterImageFormData {
  image_description: string;
  image_address: string;
  image_url: string;
  expiry_date: string;
}

export interface FooterImageFormErrors {
  image_description?: string;
  image_address?: string;
  image_url?: string;
  expiry_date?: string;
}

interface FooterImageFormProps {
  formData: FooterImageFormData;
  onFormChange: (field: string, value: any) => void;
  errors?: FooterImageFormErrors;
  disabled?: boolean;
  showImagePreview?: boolean;
}

// Helper function to get minimum date (today)
const getMinDate = () => getTodayStartOfDay();

// Image Preview Component that updates when URL changes
const ImagePreviewComponent: React.FC<{ imageUrl: string; altText: string }> = ({ imageUrl, altText }) => {
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    setImageError(false);
  }, [imageUrl]);

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
        Image Preview
      </Typography>
      <Box sx={{ mt: 1 }}>
        {imageError ? (
          <Box
            sx={{
              p: 2,
              border: '1px dashed',
              borderColor: 'divider',
              borderRadius: 1,
              textAlign: 'center',
              color: 'text.secondary',
            }}
          >
            <Typography variant="caption">Failed to load image</Typography>
          </Box>
        ) : (
          <img
            key={imageUrl}
            src={imageUrl}
            alt={altText}
            style={{ maxWidth: '100%', borderRadius: 4 }}
            onError={() => setImageError(true)}
            onLoad={() => setImageError(false)}
          />
        )}
      </Box>
    </Box>
  );
};

const FooterImageForm: React.FC<FooterImageFormProps> = ({
  formData,
  onFormChange,
  errors = {},
  disabled = false,
  showImagePreview = false,
}) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    onFormChange(name, value);
  };

  const handleDateChange = (date: string | null) => {
    onFormChange('expiry_date', date || '');
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Image Description */}
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
          Image Description *
        </Typography>
        <TextField
          fullWidth
          name="image_description"
          value={formData.image_description}
          onChange={handleChange}
          variant="outlined"
          size="small"
          placeholder="Enter image description"
          error={!!errors.image_description}
          helperText={errors.image_description}
          disabled={disabled}
          required
          inputProps={{
            sx: { py: 1.5 },
          }}
        />
      </Box>

      {/* Image Address */}
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
          Image Address *
        </Typography>
        <TextField
          fullWidth
          name="image_address"
          value={formData.image_address}
          onChange={handleChange}
          variant="outlined"
          size="small"
          placeholder="Enter image URL"
          error={!!errors.image_address}
          helperText={errors.image_address}
          disabled={disabled}
          required
          inputProps={{
            sx: { py: 1.5 },
          }}
        />
      </Box>

      {/* Image Preview */}
      {showImagePreview && formData.image_address && (
        <ImagePreviewComponent imageUrl={formData.image_address} altText={formData.image_description || 'Footer image'} />
      )}

      {/* Landing Page URL */}
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
          Landing Page URL *
        </Typography>
        <TextField
          fullWidth
          name="image_url"
          value={formData.image_url}
          onChange={handleChange}
          variant="outlined"
          size="small"
          placeholder="Enter landing page URL"
          error={!!errors.image_url}
          helperText={errors.image_url}
          disabled={disabled}
          required
          inputProps={{
            sx: { py: 1.5 },
          }}
        />
      </Box>

      {/* Expiry Date */}
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
          Expiry Date
        </Typography>
        <SingleDatePickerMAIQ
          value={formData.expiry_date}
          onChange={handleDateChange}
          placeholder="Select expiry date"
          minDate={getMinDate()}
          disabled={disabled}
        />
        {errors.expiry_date && (
          <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75, display: 'block' }}>
            {errors.expiry_date}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default FooterImageForm;

